import React from 'react';
import { Box, Chip, Typography } from '@mui/material';

interface PageHeaderProps {
  /** Small section chip, e.g. "Sales" or "Quality" */
  section: string;
  title: string;
  subtitle?: string;
  /** Action buttons rendered on the right */
  actions?: React.ReactNode;
}

/**
 * Dark hero header (sample-layout style): section chip + date on top,
 * big title + subtitle on the left, action buttons on the right.
 */
const PageHeader: React.FC<PageHeaderProps> = ({ section, title, subtitle, actions }) => {
  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <Box
      sx={{
        bgcolor: '#1d2025',
        color: '#fff',
        borderRadius: 3,
        px: { xs: 2, md: 3 },
        py: 2.5,
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
          <Chip
            label={section}
            size="small"
            sx={{
              bgcolor: 'rgba(242,179,44,0.18)',
              color: '#f2b32c',
              fontWeight: 700,
              letterSpacing: 0.3,
            }}
          />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            {today}
          </Typography>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            '& .MuiButton-outlined': {
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.35)',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
            },
          }}
        >
          {actions}
        </Box>
      )}
    </Box>
  );
};

export default PageHeader;
